import React, { useState } from 'react';

const PREMIUMS = {
  Personal: { base: 8500, perEmployee: 0 },
  Business: { base: 15000, perEmployee: 320 },
  Enterprise: { base: 45000, perEmployee: 180 },
};
const LIABILITY_EXTRA = { '₹10 Lakhs': 2000, '₹25 Lakhs': 4500, '₹50 Lakhs': 8000, '₹1 Crore': 14000 };
const TENURE_MULTIPLIER = { '1 year': 1, '2 years': 1.85, '3 years': 2.6 };

const EXPECTED = {
  policyType: 'Business',
  brn: 'BRN-2024-98712',
  employees: '45',
  liabilityCoverage: true,
  coverageLimit: '₹50 Lakhs',
  tenure: '3 years',
};

function calcPremium(form) {
  const base = PREMIUMS[form.policyType]?.base || 0;
  const perEmp = (PREMIUMS[form.policyType]?.perEmployee || 0) * (parseInt(form.employees) || 0);
  const liability = form.liabilityCoverage ? (LIABILITY_EXTRA[form.coverageLimit] || 0) : 0;
  const multiplier = TENURE_MULTIPLIER[form.tenure] || 1;
  return Math.round((base + perEmp + liability) * multiplier);
}

export default function C12_DynamicForm({ onComplete }) {
  const [form, setForm] = useState({
    policyType: 'Personal', brn: '', employees: '', liabilityCoverage: false, coverageLimit: '₹10 Lakhs', tenure: '1 year',
  });
  const [premium, setPremium] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setPremium(null);
  };

  const handleCalculate = () => {
    const p = calcPremium(form);
    setPremium(p);
  };

  const handleSubmit = () => {
    if (!answer.trim()) return;
    setSubmitted(true);
    const expectedPremium = calcPremium(EXPECTED);
    const normNum = (s) => s.trim().replace(/[₹,\s]/g, '');
    const premiumPass = normNum(answer) === normNum(String(expectedPremium));
    const results = {
      policyType: { expected: EXPECTED.policyType, got: form.policyType, pass: form.policyType === EXPECTED.policyType },
      brn: { expected: EXPECTED.brn, got: form.brn, pass: form.brn.trim() === EXPECTED.brn },
      employees: { expected: EXPECTED.employees, got: form.employees, pass: form.employees === EXPECTED.employees },
      liabilityCoverage: { expected: 'true', got: String(form.liabilityCoverage), pass: form.liabilityCoverage === true },
      coverageLimit: { expected: EXPECTED.coverageLimit, got: form.coverageLimit, pass: form.coverageLimit === EXPECTED.coverageLimit },
      tenure: { expected: EXPECTED.tenure, got: form.tenure, pass: form.tenure === EXPECTED.tenure },
      premiumAnswer: { expected: `Rs ${expectedPremium.toLocaleString()}`, got: answer.trim(), pass: premiumPass },
    };
    const correct = Object.values(results).filter(r => r.pass).length;
    onComplete({ score: Math.round((correct / 7) * 100), details: results, passed: correct === 7 });
  };

  const isBusiness = form.policyType !== 'Personal';

  return (
    <div className="ab-challenge-ui">
      <div className="ab-form-grid-2">
        <div className="ab-field">
          <label className="ab-label">Policy Type</label>
          <select name="policyType" value={form.policyType} onChange={ch} className="ab-input" disabled={submitted}>
            {['Personal', 'Business', 'Enterprise'].map(v => <option key={v}>{v}</option>)}
          </select>
        </div>
        <div className="ab-field">
          <label className="ab-label">Tenure</label>
          <select name="tenure" value={form.tenure} onChange={ch} className="ab-input" disabled={submitted}>
            {Object.keys(TENURE_MULTIPLIER).map(v => <option key={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {isBusiness && (
        <div className="ab-conditional-section">
          <div className="ab-conditional-label">🔓 Business fields unlocked</div>
          <div className="ab-form-grid-2">
            <div className="ab-field">
              <label className="ab-label">Business Registration Number</label>
              <input name="brn" value={form.brn} onChange={ch} className="ab-input" placeholder="BRN-XXXX-XXXXX" disabled={submitted} />
            </div>
            <div className="ab-field">
              <label className="ab-label">Number of Employees</label>
              <input name="employees" type="number" value={form.employees} onChange={ch} className="ab-input" placeholder="e.g. 45" disabled={submitted} />
            </div>
          </div>
        </div>
      )}

      <div className="ab-field">
        <label className="ab-chk-label">
          <input type="checkbox" name="liabilityCoverage" checked={form.liabilityCoverage} onChange={ch} disabled={submitted} />
          Include Liability Coverage
        </label>
      </div>

      {form.liabilityCoverage && (
        <div className="ab-conditional-section">
          <div className="ab-conditional-label">🔓 Coverage limit unlocked</div>
          <div className="ab-field">
            <label className="ab-label">Coverage Limit</label>
            <select name="coverageLimit" value={form.coverageLimit} onChange={ch} className="ab-input" disabled={submitted}>
              {Object.keys(LIABILITY_EXTRA).map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
        </div>
      )}

      {!submitted && (
        <button className="ab-action-btn" onClick={handleCalculate}>Calculate Premium</button>
      )}

      {premium !== null && (
        <div className="ab-premium-result">
          <span>Calculated Premium:</span>
          <strong className="ab-premium-val">₹{premium.toLocaleString()}</strong>
          <span className="ab-premium-sub">per policy period</span>
        </div>
      )}

      {premium !== null && !submitted && (
        <div className="ab-answer-row">
          <label className="ab-label">Enter the calculated premium shown above:</label>
          <input value={answer} onChange={e => setAnswer(e.target.value)} className="ab-input ab-answer-input" placeholder="e.g. 1,23,456" />
          <button className="ab-action-btn" onClick={handleSubmit}>Submit Answer</button>
        </div>
      )}
      {submitted && <div className="ab-submitted-badge">✓ Answer Submitted — Evaluating...</div>}
    </div>
  );
}
